import { faChevronDown, faChevronRight, faCube, faFileInvoiceDollar, faFolder, faFolderOpen, faRulerHorizontal } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatCurrencyToLocale } from "../../../../../utils/method";
import ExpandablePositionsTable from "./ExpandablePositionsTable";
import './RecursiveGroupRenderer.scss';

const RecursiveGroupRenderer = ({
    element,
    level = 0,
    expandedGroups,
    expandedMeasurements,
    toggleGroup,
    toggleMeasurement,
    onMeasurementChange,
    isReadonly = false,
    positionsData = {},
}) => {
    const { t } = useTranslation();

    const getAmountStatusClass = (measuredAmount, invoiceAmount) => {
        if (!invoiceAmount || invoiceAmount === 0) return 'amount-status--neutral';
        const diff = Math.abs(measuredAmount - invoiceAmount);
        if (diff < 0.01) return 'amount-status--matched';
        if (measuredAmount > invoiceAmount) return 'amount-status--exceeded';
        return 'amount-status--insufficient';
    };

    if (!element.isGroup) return null;

    const isExpanded = expandedGroups.has(element.id);
    const positions = element.elements ? element.elements.filter(el => !el.isGroup) : [];
    const subGroups = element.elements ? element.elements.filter(el => el.isGroup) : [];

    const hasInvalidated = positions.some(pos => pos.isValidated === false);

    const calculateTotalMeasuredAmount = (el) => {
        if (!el.elements) return 0;
        const direct = el.elements.filter(e => !e.isGroup)
            .reduce((sum, pos) => sum + ((positionsData[pos.id] || {}).aufmass?.amount || 0), 0);
        return direct + el.elements.filter(e => e.isGroup)
            .reduce((sum, sg) => sum + calculateTotalMeasuredAmount(sg), 0);
    };

    const calculateTotalInvoiceAmount = (el) => {
        if (!el.elements) return 0;
        const direct = el.elements.filter(e => !e.isGroup)
            .reduce((sum, pos) => sum + ((positionsData[pos.id] || {}).invoice?.amount || 0), 0);
        return direct + el.elements.filter(e => e.isGroup)
            .reduce((sum, sg) => sum + calculateTotalInvoiceAmount(sg), 0);
    };

    const measuredTotal = calculateTotalMeasuredAmount(element);
    const invoiceTotal = calculateTotalInvoiceAmount(element);

    // Nesting level capped at 4 for the border-colour cycle (levels 5+ reuse level 0 border)
    const borderLevel = level % 5;
    const indentLevel = Math.min(level, 4);

    return (
        <div className={`recursive-group recursive-group--level-${indentLevel}`}>
            {/* Group Header */}
            <div
                className={`recursive-group__header recursive-group__header--border-${borderLevel} d-flex justify-content-between align-items-center`}
                onClick={() => toggleGroup(element.id)}
            >
                <div className="d-flex align-items-center">
                    <div className="me-2 recursive-group__chevron-container">
                        <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                            className="group-icon-sm"
                        />
                    </div>
                    <FontAwesomeIcon
                        icon={isExpanded ? faFolderOpen : faFolder}
                        className="me-2 group-icon-md"
                    />
                    <h6 className="mb-0 fw-semibold recursive-group__title">
                        {element.name}
                        {hasInvalidated && (
                            <span
                                className="recursive-group__invalidated-badge"
                                title={t('aufmass.invalidation-info')}
                            >
                                {t('aufmass.invalidated')}
                            </span>
                        )}
                    </h6>
                </div>
                <div className="d-flex align-items-center">
                    {positions.length > 0 && (
                        <Badge className="me-2 px-2 py-1 recursive-group__positions-badge">
                            <FontAwesomeIcon icon={faCube} className="me-1" size="xs" />
                            {positions.length} {t('hierarchical-positions.positions')}
                        </Badge>
                    )}
                    {subGroups.length > 0 && (
                        <Badge className="me-2 px-2 py-1 recursive-group__subgroups-badge">
                            <FontAwesomeIcon icon={faFolder} className="me-1" size="xs" />
                            {subGroups.length} {t('hierarchical-positions.groups')}
                        </Badge>
                    )}
                    <div className="recursive-group__amount-container">
                        <div className="px-2 py-1 recursive-group__invoice-amount">
                            <FontAwesomeIcon icon={faFileInvoiceDollar} /> {formatCurrencyToLocale(invoiceTotal)}
                        </div>
                        <div className={`px-2 py-1 amount-status-badge amount-status-badge--border-dark ${getAmountStatusClass(measuredTotal, invoiceTotal)}`}>
                            <FontAwesomeIcon icon={faRulerHorizontal} /> {formatCurrencyToLocale(measuredTotal)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && element.elements && (
                <div className="recursive-group__body">
                    {subGroups.map(subGroup => (
                        <RecursiveGroupRenderer
                            key={subGroup.id}
                            element={subGroup}
                            level={level + 1}
                            expandedGroups={expandedGroups}
                            expandedMeasurements={expandedMeasurements}
                            toggleGroup={toggleGroup}
                            toggleMeasurement={toggleMeasurement}
                            onMeasurementChange={onMeasurementChange}
                            isReadonly={isReadonly}
                            positionsData={positionsData}
                        />
                    ))}

                    {positions.length > 0 && (
                        <div className={`recursive-group__positions-table${subGroups.length > 0 ? " recursive-group__positions-table--top-margin" : ""}`}>
                            <ExpandablePositionsTable
                                positions={positions}
                                expandedMeasurements={expandedMeasurements}
                                toggleMeasurement={toggleMeasurement}
                                onMeasurementChange={onMeasurementChange}
                                isReadonly={isReadonly}
                                positionsData={positionsData}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RecursiveGroupRenderer;
